import React, { useContext, useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Box, Button, Divider, FormLabel, IconButton,
  InputAdornment, Link, Stack, TextField, Typography,
} from '@mui/material';
import VisibilityRoundedIcon    from '@mui/icons-material/VisibilityRounded';
import VisibilityOffRoundedIcon from '@mui/icons-material/VisibilityOffRounded';
import SpaRoundedIcon           from '@mui/icons-material/SpaRounded';
import axios from 'axios';
import { AuthContext } from '../components/signin/AuthContext';
import Auth from '../components/signin/auth/auth';
import ForgotPassword from '../components/signin/ForgotPassword';
import { V4ThemeProvider, V4_VARIANT } from './theme';
import ColorModeToggle from './components/ColorModeToggle';
import ThemePicker     from './components/ThemePicker';

const ANIM = `
@keyframes bgSpin {
  from { transform:rotate(0deg); }
  to   { transform:rotate(360deg); }
}
@keyframes v4CardIn {
  from { opacity:0; transform:translateY(32px) scale(0.97); }
  to   { opacity:1; transform:translateY(0)    scale(1);    }
}
@keyframes scanLine {
  0%   { top:-2px; opacity:0; }
  10%  { opacity:0.5; }
  90%  { opacity:0.5; }
  100% { top:100%; opacity:0; }
}
`;

export default function V4SignIn({ variant = V4_VARIANT }) {
  const home = `${variant.basePath}/dashboard`;
  const navigate = useNavigate();
  const { token, setToken } = useContext(AuthContext);
  const [email,  setEmail]  = useState('');
  const [pw,     setPw]     = useState('');
  const [showPw, setShowPw] = useState(false);
  const [err,    setErr]    = useState('');
  const [forgot, setForgot] = useState(false);

  useEffect(() => { if (token) navigate(home); }, [token, navigate, home]);
  if (localStorage.getItem('authenticated') === 'true' && token)
    return <Navigate to={home} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pw || pw.length < 6) { setErr('Mindestens 6 Zeichen.'); return; }
    setErr('');
    try {
      const res = await axios.post('/login_password', { username: email, password: pw, remember: true });
      setToken(res.data.access_token);
      localStorage.setItem('authenticated', 'true');
      navigate(home);
    } catch { setErr('Anmeldung fehlgeschlagen.'); }
  };

  return (
    <V4ThemeProvider variant={variant}>
      <style>{ANIM}</style>

      {/* Background */}
      <Box sx={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--v4-bg)', position:'relative', overflow:'hidden', p:2 }}>
        <Stack direction="row" spacing={0.75} sx={{ position:'absolute', top:16, right:16, zIndex:2 }}>
          <ThemePicker />
          <ColorModeToggle />
        </Stack>
        {/* Spinning ring */}
        <Box sx={{
          position:'absolute', width:700, height:700, borderRadius:'50%',
          border:'1px solid rgba(var(--v4-cyan-rgb),0.06)',
          animation:'bgSpin 30s linear infinite',
          '&::before':{ content:'""', position:'absolute', top:'10%', left:'-1px', width:2, height:'25%', background:'linear-gradient(to bottom,transparent,var(--v4-cyan),transparent)' },
        }} />
        <Box sx={{
          position:'absolute', width:450, height:450, borderRadius:'50%',
          border:'1px solid rgba(var(--v4-green-rgb),0.04)',
          animation:'bgSpin 20s linear infinite reverse',
        }} />
        {/* Glow */}
        <Box sx={{ position:'absolute', top:'30%', left:'50%', transform:'translateX(-50%)', width:600, height:300, background:'radial-gradient(ellipse,rgba(var(--v4-cyan-rgb),0.08) 0%,transparent 70%)', filter:'blur(40px)', pointerEvents:'none' }} />

        {/* Card */}
        <Box sx={{
          width:'100%', maxWidth:400, position:'relative', zIndex:1,
          animation:'v4CardIn 0.6s cubic-bezier(0.22,1,0.36,1) both',
        }}>
          <Box sx={{
            background:'rgba(var(--v4-surface2-rgb),0.92)',
            border:'1px solid rgba(var(--v4-cyan-rgb),0.15)',
            borderRadius:'24px',
            backdropFilter:'blur(24px)',
            boxShadow:'0 32px 80px rgba(var(--v4-shadow-rgb),calc(0.7*var(--v4-shadow-k))), inset 0 1px 0 rgba(var(--v4-cyan-rgb),0.07)',
            p:{ xs:3, sm:4 },
            position:'relative',
            overflow:'hidden',
          }}>
            {/* Scan line */}
            <Box sx={{
              position:'absolute', left:0, right:0, height:2,
              background:'linear-gradient(90deg,transparent 0%,rgba(var(--v4-cyan-rgb),0.4) 50%,transparent 100%)',
              animation:'scanLine 4s ease-in-out infinite',
              pointerEvents:'none',
            }} />

            {/* Logo */}
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb:4 }}>
              <Box sx={{
                width:46, height:46, borderRadius:'14px',
                background:'linear-gradient(135deg,var(--v4-cyan) 0%,var(--v4-cyan-deep) 100%)',
                display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow:'0 8px 28px rgba(var(--v4-cyan-rgb),0.45)',
              }}>
                <SpaRoundedIcon sx={{ color:'#fff', fontSize:24 }} />
              </Box>
              <Box>
                <Typography sx={{ fontWeight:800, fontSize:'1.3rem', color:'var(--v4-cyan)', lineHeight:1.1, letterSpacing:'-0.01em' }}>
                  {variant.brand}
                </Typography>
                <Typography sx={{ fontSize:'0.65rem', color:'rgba(var(--v4-cyan-rgb),0.4)', letterSpacing:'0.15em', textTransform:'uppercase' }}>
                  Grow Monitoring
                </Typography>
              </Box>
            </Stack>

            <Typography variant="h6" sx={{ fontWeight:700, mb:0.5, color:'rgba(var(--v4-text-rgb),0.95)' }}>
              Anmelden
            </Typography>
            <Typography variant="body2" sx={{ color:'rgba(var(--v4-muted-rgb),0.45)', mb:3 }}>
              Überwache dein Grow-Setup
            </Typography>

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2}>
                <Box>
                  <FormLabel sx={{ fontSize:'0.78rem', color:'rgba(var(--v4-muted-rgb),0.55)', display:'block', mb:0.75 }}>
                    E-Mail / Benutzername
                  </FormLabel>
                  <TextField
                    type="text" value={email} onChange={e => setEmail(e.target.value)}
                    placeholder="deine@email.com" fullWidth required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background:'rgba(var(--v4-cyan-rgb),0.03)', borderRadius:'12px',
                        fontFamily:'"Inter",sans-serif',
                        '& fieldset': { borderColor:'rgba(var(--v4-cyan-rgb),0.12)' },
                        '&:hover fieldset': { borderColor:'rgba(var(--v4-cyan-rgb),0.3)' },
                        '&.Mui-focused fieldset': { borderColor:'var(--v4-cyan)' },
                      },
                    }}
                  />
                </Box>

                <Box>
                  <Stack direction="row" justifyContent="space-between" sx={{ mb:0.75 }}>
                    <FormLabel sx={{ fontSize:'0.78rem', color:'rgba(var(--v4-muted-rgb),0.55)' }}>Passwort</FormLabel>
                    <Link component="button" type="button" variant="caption" onClick={() => setForgot(true)}
                      sx={{ color:'var(--v4-cyan)', '&:hover':{ color:'var(--v4-cyan-light)' } }}>
                      Vergessen?
                    </Link>
                  </Stack>
                  <TextField
                    type={showPw ? 'text' : 'password'} value={pw} onChange={e => setPw(e.target.value)}
                    placeholder="••••••••" fullWidth required
                    error={!!err} helperText={err}
                    InputProps={{
                      endAdornment:(
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowPw(p=>!p)} sx={{ color:'rgba(var(--v4-muted-rgb),0.35)' }}>
                            {showPw ? <VisibilityOffRoundedIcon fontSize="small" /> : <VisibilityRoundedIcon fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        background:'rgba(var(--v4-cyan-rgb),0.03)', borderRadius:'12px',
                        '& fieldset': { borderColor:'rgba(var(--v4-cyan-rgb),0.12)' },
                        '&:hover fieldset': { borderColor:'rgba(var(--v4-cyan-rgb),0.3)' },
                        '&.Mui-focused fieldset': { borderColor:'var(--v4-cyan)' },
                      },
                    }}
                  />
                </Box>

                <ForgotPassword open={forgot} handleClose={() => setForgot(false)} />

                <Button
                  type="submit" fullWidth variant="contained"
                  sx={{
                    py:1.5, fontWeight:700, fontSize:'0.95rem', borderRadius:'12px',
                    background:'linear-gradient(135deg,var(--v4-cyan) 0%,#0077aa 100%)',
                    boxShadow:'0 6px 24px rgba(var(--v4-cyan-rgb),0.4)',
                    letterSpacing:'0.02em',
                    '&:hover':{ background:'linear-gradient(135deg,#33deff 0%,#0099cc 100%)', boxShadow:'0 8px 30px rgba(var(--v4-cyan-rgb),0.55)', transform:'translateY(-1px)' },
                    transition:'all 0.18s ease',
                  }}
                >
                  Anmelden
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ my:2.5, borderColor:'rgba(var(--v4-cyan-rgb),0.07)', '&::before,&::after':{ borderColor:'rgba(var(--v4-cyan-rgb),0.07)' } }}>
              <Typography variant="caption" sx={{ color:'rgba(var(--v4-muted-rgb),0.25)', px:1 }}>oder</Typography>
            </Divider>

            <Box sx={{ mb:2 }}><Auth /></Box>

            <Typography variant="body2" sx={{ textAlign:'center', color:'rgba(var(--v4-muted-rgb),0.4)' }}>
              Noch kein Konto?{' '}
              <Link href="/signup" sx={{ color:'var(--v4-cyan)', fontWeight:600, '&:hover':{ color:'var(--v4-cyan-light)' } }}>
                Registrieren
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </V4ThemeProvider>
  );
}
